import { useState, useEffect, useCallback } from 'react'
import {
  BookMarked,
  RefreshCw,
  Send,
  Play,
  Presentation,
  ExternalLink,
  Trash2,
  AlertCircle,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  Database,
  FileSearch,
  Brain,
  DollarSign,
  Sparkles,
  Zap,
} from 'lucide-react'
import { KaijuLoader } from '../components/KaijuLoader'

// ============================================================================
// API Helper
// ============================================================================

const API = 'http://localhost:3003/api/notebook'

const apiFetch = async (path: string, options?: RequestInit) => {
  const res = await fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  })
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText)
  return res.json()
}

// ============================================================================
// Constants
// ============================================================================

const domainTabs = [
  { key: 'all', label: 'All', icon: BookMarked },
  { key: 'it_spending', label: 'IT Spending', icon: Database },
  { key: 'strategic_plans', label: 'Strategic Plans', icon: FileSearch },
  { key: 'knowledge', label: 'Knowledge', icon: Brain },
] as const

const domainColors: Record<string, string> = {
  it_spending: 'bg-blue-100 text-blue-700',
  strategic_plans: 'bg-green-100 text-green-700',
  knowledge: 'bg-purple-100 text-purple-700',
}

const statusColors: Record<string, string> = {
  ready: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  stale: 'bg-orange-100 text-orange-700',
  error: 'bg-red-100 text-red-700',
}

const statusIcons: Record<string, typeof CheckCircle> = {
  ready: CheckCircle,
  pending: Clock,
  stale: AlertCircle,
  error: AlertCircle,
}

// ============================================================================
// Main Component
// ============================================================================

export function UnifiedNotebookPage() {
  // State
  const [notebook, setNotebook] = useState<any>(null)
  const [sources, setSources] = useState<any[]>([])
  const [queries, setQueries] = useState<any[]>([])
  const [artifacts, setArtifacts] = useState<any[]>([])
  const [activeTab, setActiveTab] = useState<string>('all')
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState<string | null>(null)
  const [queryText, setQueryText] = useState('')
  const [queryScope, setQueryScope] = useState<string>('')
  const [queryLoading, setQueryLoading] = useState(false)
  const [currentAnswer, setCurrentAnswer] = useState<any>(null)
  const [writeBackActions, setWriteBackActions] = useState<any[] | null>(null)
  const [writeBackLoading, setWriteBackLoading] = useState(false)
  const [expandedQueries, setExpandedQueries] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [_syncLogs, setSyncLogs] = useState<any[]>([])
  const [showAddSource, setShowAddSource] = useState(false)
  const [newSourceType, setNewSourceType] = useState('url')
  const [newSourceUri, setNewSourceUri] = useState('')
  const [addSourceLoading, setAddSourceLoading] = useState(false)
  const [showStudio, setShowStudio] = useState(false)
  const [studioLoading, setStudioLoading] = useState<string | null>(null)
  const [selectedWriteBackActions, setSelectedWriteBackActions] = useState<Set<number>>(new Set())
  const [writeBackResults, setWriteBackResults] = useState<any[] | null>(null)

  // ============================================================================
  // Data Loading
  // ============================================================================

  const fetchNotebook = useCallback(async () => {
    try {
      const data = await apiFetch('/')
      const nb = data.notebook ?? data
      setNotebook(nb)
      return nb
    } catch {
      setNotebook(null)
      return null
    }
  }, [])

  const fetchSources = useCallback(async () => {
    try {
      const data = await apiFetch('/sources')
      setSources(data.sources || data || [])
    } catch {
      setSources([])
    }
  }, [])

  const fetchQueries = useCallback(async () => {
    try {
      const data = await apiFetch('/queries')
      setQueries(data.queries || data || [])
    } catch {
      setQueries([])
    }
  }, [])

  const fetchArtifacts = useCallback(async () => {
    try {
      const data = await apiFetch('/studio')
      setArtifacts(data.artifacts || data || [])
    } catch {
      setArtifacts([])
    }
  }, [])

  useEffect(() => {
    const init = async () => {
      setLoading(true)
      setError(null)
      try {
        const nb = await fetchNotebook()
        if (nb && nb.id) {
          await Promise.all([fetchSources(), fetchQueries(), fetchArtifacts()])
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load notebook')
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [fetchNotebook, fetchSources, fetchQueries, fetchArtifacts])

  // ============================================================================
  // Actions
  // ============================================================================

  async function handleCreateNotebook() {
    setError(null)
    try {
      const data = await apiFetch('/create', { method: 'POST', body: JSON.stringify({}) })
      setNotebook(data.notebook ?? data)
      await Promise.all([fetchSources(), fetchQueries(), fetchArtifacts()])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create notebook')
    }
  }

  async function handleSync(domain?: string) {
    const syncKey = domain || 'all'
    setSyncing(syncKey)
    setError(null)
    try {
      const path = domain ? `/sync/${domain}` : '/sync'
      const data = await apiFetch(path, { method: 'POST', body: JSON.stringify({}) })
      if (data.logs) setSyncLogs(data.logs)
      await fetchSources()
      await fetchNotebook()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Sync failed for ${syncKey}`)
    } finally {
      setSyncing(null)
    }
  }

  async function handleQuery() {
    if (!queryText.trim()) return
    setQueryLoading(true)
    setCurrentAnswer(null)
    setError(null)
    try {
      const data = await apiFetch('/query', {
        method: 'POST',
        body: JSON.stringify({ question: queryText.trim(), domain: queryScope || undefined }),
      })
      setCurrentAnswer(data.query ?? data)
      setQueryText('')
      await fetchQueries()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Query failed')
    } finally {
      setQueryLoading(false)
    }
  }

  async function handleExtractActions() {
    if (!currentAnswer) return
    setWriteBackLoading(true)
    setWriteBackActions(null)
    setWriteBackResults(null)
    try {
      const data = await apiFetch('/writeback/parse', {
        method: 'POST',
        body: JSON.stringify({ queryId: currentAnswer.id, answer: currentAnswer.answer }),
      })
      const actions = data.actions || data || []
      setWriteBackActions(actions)
      setSelectedWriteBackActions(new Set(actions.map((_: any, i: number) => i)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to extract actions')
    } finally {
      setWriteBackLoading(false)
    }
  }

  async function handleExecuteWriteBack() {
    if (!writeBackActions) return
    setWriteBackLoading(true)
    setWriteBackResults(null)
    try {
      const selected = writeBackActions.filter((_, i) => selectedWriteBackActions.has(i))
      const data = await apiFetch('/writeback/execute', {
        method: 'POST',
        body: JSON.stringify({ actions: selected }),
      })
      setWriteBackResults(data.results || data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Write-back execution failed')
    } finally {
      setWriteBackLoading(false)
    }
  }

  async function handleDeleteSource(sourceId: string) {
    setError(null)
    try {
      await apiFetch(`/sources/${sourceId}`, { method: 'DELETE' })
      await fetchSources()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete source')
    }
  }

  async function handleAddSource() {
    if (!newSourceUri.trim()) return
    setAddSourceLoading(true)
    setError(null)
    try {
      await apiFetch('/sources', {
        method: 'POST',
        body: JSON.stringify({ sourceType: newSourceType, uri: newSourceUri.trim() }),
      })
      setNewSourceUri('')
      setShowAddSource(false)
      await fetchSources()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add source')
    } finally {
      setAddSourceLoading(false)
    }
  }

  async function handleStudioAction(action: string) {
    setStudioLoading(action)
    setError(null)
    try {
      await apiFetch(`/studio/${action}`, { method: 'POST', body: JSON.stringify({}) })
      await fetchArtifacts()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Studio action "${action}" failed`)
    } finally {
      setStudioLoading(null)
    }
  }

  function toggleQueryExpand(queryId: string) {
    setExpandedQueries((prev) => {
      const next = new Set(prev)
      if (next.has(queryId)) {
        next.delete(queryId)
      } else {
        next.add(queryId)
      }
      return next
    })
  }

  function toggleWriteBackAction(index: number) {
    setSelectedWriteBackActions((prev) => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }

  // ============================================================================
  // Derived Data
  // ============================================================================

  const filteredSources =
    activeTab === 'all' ? sources : sources.filter((s) => s.domain === activeTab)

  const sourceCountByDomain = (domain: string) =>
    domain === 'all' ? sources.length : sources.filter((s) => s.domain === domain).length

  const staleSources = sources.filter((s) => s.status === 'stale')

  const notebookStatus = notebook?.status || 'none'
  const notebookStatusColor =
    notebookStatus === 'active'
      ? 'bg-green-100 text-green-700'
      : notebookStatus === 'error'
        ? 'bg-red-100 text-red-700'
        : 'bg-gray-100 text-gray-600'

  // ============================================================================
  // Render
  // ============================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookMarked className="w-7 h-7 text-indigo-600" />
            Unified Intelligence Notebook
          </h1>
          <div className="flex items-center gap-3 mt-1">
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${notebookStatusColor}`}>
              {notebookStatus}
            </span>
            {notebook?.lastSyncedAt && (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Last synced: {new Date(notebook.lastSyncedAt).toLocaleString()}
              </span>
            )}
            <span className="text-xs text-gray-500">
              {sources.length} source{sources.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {notebook?.notebookUrl && (
            <a
              href={notebook.notebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Open in NotebookLM
            </a>
          )}
          {!notebook?.id && (
            <button
              onClick={handleCreateNotebook}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Create Notebook
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-xs underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Toolbar */}
      {notebook?.id && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => handleSync()}
              disabled={syncing !== null}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {syncing === 'all' ? (
                <KaijuLoader size="sm" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Sync All
            </button>

            <div className="h-6 w-px bg-gray-200" />

            <button
              onClick={() => handleSync('it_spending')}
              disabled={syncing !== null}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1.5"
            >
              {syncing === 'it_spending' ? (
                <KaijuLoader size="sm" />
              ) : (
                <DollarSign className="w-3.5 h-3.5" />
              )}
              IT Spending
            </button>
            <button
              onClick={() => handleSync('strategic_plans')}
              disabled={syncing !== null}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1.5"
            >
              {syncing === 'strategic_plans' ? (
                <KaijuLoader size="sm" />
              ) : (
                <FileSearch className="w-3.5 h-3.5" />
              )}
              Strategic Plans
            </button>
            <button
              onClick={() => handleSync('knowledge')}
              disabled={syncing !== null}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1.5"
            >
              {syncing === 'knowledge' ? (
                <KaijuLoader size="sm" />
              ) : (
                <Brain className="w-3.5 h-3.5" />
              )}
              Knowledge
            </button>

            {staleSources.length > 0 && (
              <>
                <div className="h-6 w-px bg-gray-200" />
                <span className="text-xs text-orange-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {staleSources.length} stale source{staleSources.length !== 1 ? 's' : ''}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Domain Tabs */}
      {notebook?.id && (
        <div className="border-b border-gray-200">
          <div className="flex">
            {domainTabs.map((tab) => {
              const Icon = tab.icon
              const count = sourceCountByDomain(tab.key)
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'text-indigo-600 border-indigo-500 bg-indigo-50/50'
                      : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full ${
                      activeTab === tab.key
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Two-Column Layout */}
      {notebook?.id && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left Column: Query Panel */}
          <div className="lg:col-span-3 space-y-4">
            {/* Query Input */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex gap-3">
                <div className="flex-1 space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={queryText}
                      onChange={(e) => setQueryText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleQuery()}
                      placeholder="Ask a question across all domains..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      onClick={handleQuery}
                      disabled={queryLoading || !queryText.trim()}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {queryLoading ? (
                        <KaijuLoader size="sm" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Ask
                    </button>
                  </div>
                  <select
                    value={queryScope}
                    onChange={(e) => setQueryScope(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-600 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">All Domains</option>
                    <option value="it_spending">IT Spending</option>
                    <option value="strategic_plans">Strategic Plans</option>
                    <option value="knowledge">Knowledge</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Current Answer */}
            {currentAnswer && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-indigo-600" />
                    Answer
                  </h3>
                  <button
                    onClick={handleExtractActions}
                    disabled={writeBackLoading}
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {writeBackLoading ? (
                      <KaijuLoader size="sm" />
                    ) : (
                      <Sparkles className="w-3.5 h-3.5" />
                    )}
                    Extract Actions
                  </button>
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {currentAnswer.answer}
                </div>
                {currentAnswer.citations && currentAnswer.citations.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="text-xs text-gray-500 mb-1.5">Citations</div>
                    <div className="flex flex-wrap gap-1.5">
                      {currentAnswer.citations.map((cite: any, i: number) => (
                        <span
                          key={i}
                          className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded"
                        >
                          {cite.title || cite.source || `[${i + 1}]`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Query History */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900">Query History</h3>
              </div>
              <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
                {queries.length === 0 ? (
                  <div className="p-6 text-center text-sm text-gray-400">
                    No queries yet. Ask a question above to get started.
                  </div>
                ) : (
                  queries.map((q: any) => {
                    const isExpanded = expandedQueries.has(q.id)
                    return (
                      <div key={q.id} className="hover:bg-gray-50">
                        <button
                          onClick={() => toggleQueryExpand(q.id)}
                          className="w-full text-left px-4 py-3 flex items-start gap-2"
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-900 truncate">{q.question}</div>
                            <div className="flex items-center gap-2 mt-1">
                              {q.domain && (
                                <span
                                  className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                    domainColors[q.domain] || 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {q.domain.replace(/_/g, ' ')}
                                </span>
                              )}
                              {q.createdAt && (
                                <span className="text-[10px] text-gray-400">
                                  {new Date(q.createdAt).toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                        {isExpanded && q.answer && (
                          <div className="px-10 pb-3">
                            <div className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">
                              {q.answer}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Source List */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900">
                  Sources
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    ({filteredSources.length})
                  </span>
                </h3>
              </div>
              <div className="max-h-[500px] overflow-y-auto divide-y divide-gray-100">
                {filteredSources.length === 0 ? (
                  <div className="p-6 text-center text-sm text-gray-400">
                    No sources in this domain.
                  </div>
                ) : (
                  filteredSources.map((source: any) => {
                    const StatusIcon = statusIcons[source.status] || Clock
                    return (
                      <div key={source.id} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {source.displayName || source.name || 'Unnamed source'}
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {source.domain && (
                                <span
                                  className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                    domainColors[source.domain] || 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {source.domain.replace(/_/g, ' ')}
                                </span>
                              )}
                              <span
                                className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5 ${
                                  statusColors[source.status] || 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                <StatusIcon className="w-2.5 h-2.5" />
                                {source.status}
                              </span>
                              {source.syncDirection && (
                                <span className="text-[10px] text-gray-400">
                                  {source.syncDirection === 'push'
                                    ? 'Push'
                                    : source.syncDirection === 'pull'
                                      ? 'Pull'
                                      : 'Bidirectional'}
                                </span>
                              )}
                            </div>
                            {source.originRecordId && (
                              <a
                                href="#"
                                className="text-[10px] text-indigo-600 hover:underline mt-1 inline-flex items-center gap-0.5"
                                onClick={(e) => e.preventDefault()}
                              >
                                <ExternalLink className="w-2.5 h-2.5" />
                                View source
                              </a>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeleteSource(source.id)}
                            className="p-1 text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
                            title="Delete source"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>

              {/* Add Source */}
              <div className="border-t border-gray-100">
                <button
                  onClick={() => setShowAddSource(!showAddSource)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-gray-500 hover:bg-gray-50"
                >
                  <span>Add Source</span>
                  {showAddSource ? (
                    <ChevronDown className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5" />
                  )}
                </button>
                {showAddSource && (
                  <div className="px-4 pb-4 space-y-2">
                    <select
                      value={newSourceType}
                      onChange={(e) => setNewSourceType(e.target.value)}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="url">URL</option>
                      <option value="document">Document</option>
                      <option value="text">Text</option>
                      <option value="api">API Endpoint</option>
                    </select>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newSourceUri}
                        onChange={(e) => setNewSourceUri(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddSource()}
                        placeholder="Enter source URI..."
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <button
                        onClick={handleAddSource}
                        disabled={addSourceLoading || !newSourceUri.trim()}
                        className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {addSourceLoading ? (
                          <KaijuLoader size="sm" />
                        ) : (
                          'Add'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Write-Back Modal */}
      {writeBackActions !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Zap className="w-5 h-5 text-indigo-600" />
                Write-Back Actions
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                Select actions to execute against source systems
              </p>
            </div>
            <div className="px-6 py-4 max-h-[50vh] overflow-y-auto space-y-3">
              {writeBackActions.length === 0 ? (
                <div className="text-sm text-gray-400 text-center py-4">
                  No actions extracted from the answer.
                </div>
              ) : (
                writeBackActions.map((action: any, i: number) => (
                  <label
                    key={i}
                    className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedWriteBackActions.has(i)}
                      onChange={() => toggleWriteBackAction(i)}
                      className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs px-2 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700">
                          {action.type || action.actionType || 'update'}
                        </span>
                        {action.targetDomain && (
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                              domainColors[action.targetDomain] || 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {action.targetDomain.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-700">{action.description}</div>
                    </div>
                  </label>
                ))
              )}

              {/* Write-Back Results */}
              {writeBackResults && (
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase">Results</div>
                  {writeBackResults.map((result: any, i: number) => (
                    <div
                      key={i}
                      className={`flex items-center gap-2 text-xs p-2 rounded ${
                        result.success
                          ? 'bg-green-50 text-green-700'
                          : 'bg-red-50 text-red-700'
                      }`}
                    >
                      {result.success ? (
                        <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      )}
                      <span>{result.message || (result.success ? 'Success' : 'Failed')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setWriteBackActions(null)
                  setWriteBackResults(null)
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                {writeBackResults ? 'Close' : 'Cancel'}
              </button>
              {!writeBackResults && writeBackActions.length > 0 && (
                <button
                  onClick={handleExecuteWriteBack}
                  disabled={writeBackLoading || selectedWriteBackActions.size === 0}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {writeBackLoading ? (
                    <KaijuLoader size="sm" />
                  ) : (
                    <Zap className="w-4 h-4" />
                  )}
                  Execute Selected ({selectedWriteBackActions.size})
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Studio Panel */}
      {notebook?.id && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <button
            onClick={() => setShowStudio(!showStudio)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
          >
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-indigo-600" />
              Studio
            </h3>
            {showStudio ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
          </button>
          {showStudio && (
            <div className="px-4 pb-4 border-t border-gray-100 pt-4">
              <div className="flex gap-3 mb-4">
                <button
                  onClick={() => handleStudioAction('audio')}
                  disabled={studioLoading !== null}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50 flex items-center gap-2"
                >
                  {studioLoading === 'audio' ? (
                    <KaijuLoader size="sm" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  Generate Audio Overview
                </button>
                <button
                  onClick={() => handleStudioAction('slides')}
                  disabled={studioLoading !== null}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50 flex items-center gap-2"
                >
                  {studioLoading === 'slides' ? (
                    <KaijuLoader size="sm" />
                  ) : (
                    <Presentation className="w-4 h-4" />
                  )}
                  Generate Slide Deck
                </button>
              </div>

              {/* Existing Artifacts */}
              {artifacts.length > 0 ? (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase">Artifacts</div>
                  {artifacts.map((artifact: any) => (
                    <div
                      key={artifact.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        {artifact.type === 'audio' ? (
                          <Play className="w-4 h-4 text-gray-400" />
                        ) : (
                          <Presentation className="w-4 h-4 text-gray-400" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {artifact.name || artifact.type}
                          </div>
                          {artifact.createdAt && (
                            <div className="text-[10px] text-gray-400">
                              {new Date(artifact.createdAt).toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                      <span
                        className={`text-xs px-2 py-0.5 rounded font-medium ${
                          artifact.status === 'ready'
                            ? 'bg-green-100 text-green-700'
                            : artifact.status === 'processing'
                              ? 'bg-blue-100 text-blue-700'
                              : artifact.status === 'error'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {artifact.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-gray-400 text-center py-2">
                  No artifacts generated yet.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* No Notebook State */}
      {!notebook?.id && !loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <BookMarked className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">No Notebook Created</h2>
          <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
            Create a unified notebook to aggregate intelligence from IT Spending, Strategic Plans,
            and Knowledge domains with bidirectional sync.
          </p>
          <button
            onClick={handleCreateNotebook}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 inline-flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            Create Notebook
          </button>
        </div>
      )}
    </div>
  )
}
